/**
 * Keeps a view that <KeepAlive> holds on to from showing old data for ever. A cached view mounts once, so its own
 * onMounted fetch never runs again; this asks it to refresh when the patron comes back to it, or returns to the tab
 * while it is showing, once what it holds is older than maxAgeMs.
 *
 * @changelog
 *
 * 2026-10-10 (0.1.0) (jhoward)
 *   - Add App\Helpers\useRevalidateOnActivate().
 */

import { useDocumentVisibility } from '@vueuse/core';
import { onActivated, onDeactivated, watch } from 'vue';

export default function useRevalidateOnActivate(revalidate: () => Promise<unknown>, maxAgeMs: number) {
  //performance.now(), as useMaintenanceWindow() has it: unlike Date.now() a changed system clock cannot make data
  //look younger or older than it is.
  let freshAt = performance.now();
  let isActive = false;
  let isRevalidating = false;
  const visibility = useDocumentVisibility();

  /** Call when the view fetched for its own reasons, so a refresh is not made straight after. */
  function markFresh(): void {
    freshAt = performance.now();
  }

  async function revalidateIfStale(): Promise<void> {
    if (isRevalidating || performance.now() - freshAt < maxAgeMs) {
      return;
    }

    isRevalidating = true;
    try {
      await revalidate();
      markFresh();
    } catch {
      //Keep what is on screen. The proxy has already reported the failure, and the next visit tries again.
    } finally {
      isRevalidating = false;
    }
  }

  //Also runs just after mount, when the data is brand new and nothing is stale, so the first visit costs nothing extra.
  onActivated(() => {
    isActive = true;
    void revalidateIfStale();
  });

  onDeactivated(() => {
    isActive = false;
  });

  watch(visibility, (state) => {
    if (isActive && 'visible' === state) {
      void revalidateIfStale();
    }
  });

  return { markFresh };
}
